import { defineStore } from 'pinia'
import { computed, ComputedRef, reactive, Ref, ref, watchEffect } from 'vue'
import moment from 'moment/moment'
import calculateWeeksOverlappingADateRange from '@/components/Timesheet/utilities/calculateWeeksOverlappingADateRange'

import { RateType } from '@/enums'
import {
  CustomFieldTypeValueInput,
  TimesheetRegistrationDurationUnit,
  TimesheetRegistrationType,
} from '@/gql/graphql'
import {
  DateEntry,
  TableDateEntries,
  Timesheet,
  ParserRegistration,
} from '@/components/Timesheet/types'
import { useFormattedTimesheetRegistrationsStore } from '@/components/Timesheet/store/useFormattedTimesheetRegistrationsStore'
import { useTimesheetLinesStore } from '@/components/Timesheet/store/useTimesheetLinesStore'
import formatFixedDecimal from '@/components/Timesheet/utilities/formatFixedDecimal'
import calculateHoursFromStartAndEndTime from '@/components/Timesheet/utilities/calculateHoursFromStartAndEndTime'

export const useTimesheetFormStore = defineStore('timesheet-store', () => {
  const timesheetId = ref('')
  const isLoadingTimesheet = ref(false)
  const formattedTimesheetRegistrationsStore = useFormattedTimesheetRegistrationsStore()
  const timesheetLinesStore = useTimesheetLinesStore()

  const timesheet: Ref<Timesheet | null | undefined> = ref({
    id: '',
    startDate: null,
    endDate: null,
    createdAt: null,
    registrations: {
      data: [],
    },
    overtimes: {
      data: [],
    },
  })
  const persistedTimesheetRegistrations = ref([])
  const hasPersistedTimesheet = computed(() => !!timesheet.value?.id)

  const externallyImposedJobName = ref('')
  const externallyImposedCompanyName = ref('')

  const jobName = computed(() => timesheet.value?.hire?.job?.name || externallyImposedJobName.value)
  const companyName = computed(
    () => timesheet.value?.hire?.company.name || externallyImposedCompanyName.value,
  )
  const workerName = computed(
    () => `${timesheet.value?.hire?.worker.firstName} ${timesheet.value?.hire?.worker.lastName}`,
  )
  const issuedAt = ref('')
  const isViewOnly = ref(false)
  const rateType = ref<RateType>(RateType.HOURLY)
  const detailedView = ref(false)

  const useDayInputs = computed(() => {
    if (timesheet.value?.registrations?.data?.length) {
      return (
        timesheet.value?.registrations?.data?.[0]?.unit === TimesheetRegistrationDurationUnit.Days
      )
    }
    if (rateType.value === RateType.DAILY) {
      return true
    }
    return false
  })
  const isPersisted = ref(false)
  const useDetailedView = computed(() => {
    if (rateType.value !== RateType.HOURLY) {
      return false
    }
    // Find first registration that is not a break
    const firstNonBreaktimeRegistration = timesheet.value?.registrations?.data?.find(
      (registration) => registration.type !== TimesheetRegistrationType.Break,
    )

    if (firstNonBreaktimeRegistration) {
      return !!(firstNonBreaktimeRegistration?.startTime && firstNonBreaktimeRegistration?.endTime)
    }

    // if firstNonBreaktimeRegistration has startTime and endTime, we can assume it's a detailed view
    return !!detailedView.value
  })

  const externallyImposedStartDate = ref('')
  const externallyImposedEndDate = ref('')
  const dateStart = computed(() => externallyImposedStartDate.value || timesheet.value?.startDate)
  const dateEnd = computed(() => externallyImposedEndDate.value || timesheet.value?.endDate)
  const weekRange = computed(() =>
    calculateWeeksOverlappingADateRange({
      dateStart: dateStart.value,
      dateEnd: dateEnd.value,
    }),
  )
  // for UI purposes, we want to show the first week in the range as the default
  const firstWeekNumberInRange = computed(() => parseInt(Object.keys(weekRange.value)[0], 10))
  const selectedWeekNumber = ref()
  const userSelectedWeekNumber: Ref<undefined | number> = ref()
  // if the user has selected a week, use that, otherwise use the first week in the range
  const activeWeekNumber: ComputedRef<number> = computed(
    () => userSelectedWeekNumber.value || firstWeekNumberInRange.value,
  )
  const activeWeek = computed(() => weekRange.value[activeWeekNumber.value])

  const isDateDisabled = (dateString) => {
    // Nothing should be disabled in view only mode since nothing is editable
    if (isViewOnly.value) {
      return false
    }
    // If neither start date nor end date is present, automatically disable all dates
    if (!dateStart.value && !dateEnd.value) {
      return true
    }
    // If either timesheetFormStore.dateStart OR timesheetFormStore.dateEnd is not present, use the date that is present as both
    // the start date and end date
    const startDateForCalculation = dateStart.value ?? dateEnd.value
    const endDateForCalculation = dateEnd.value ?? dateStart.value
    return (
      moment(dateString).isBefore(moment(startDateForCalculation)) ||
      moment(dateString).isAfter(moment(endDateForCalculation))
    )
  }

  // Store the timesheet registrations in a ref
  // Entries represent a full vertical column in the table
  // when there are changes to the range of dates we are considering, update the timesheet registrations
  // to either empty or fill with the data from the API
  const tableDateEntries = ref<TableDateEntries>({})
  watchEffect(() => {
    tableDateEntries.value = {}
    Object.keys(weekRange.value).forEach((weekNumber) => {
      weekRange.value[weekNumber].dates.forEach((date) => {
        const registrationsFromApiOnDate = timesheet.value?.registrations?.data?.filter(
          (registration) => registration.date === date.format('YYYY-MM-DD'),
        )
        const breakTime =
          registrationsFromApiOnDate?.find(
            (registration) => registration.type === TimesheetRegistrationType.Break,
          )?.duration ?? 0

        // Day type registrations
        const dayRegistrations = registrationsFromApiOnDate?.filter(
          (registration) => registration.type === TimesheetRegistrationType.Day,
        )
        const dayRegistrationsPayable = dayRegistrations?.filter(
          (registration) => registration.isBillable,
        )
        const dayRegistrationsNonPayable = dayRegistrations?.filter(
          (registration) => !registration.isBillable,
        )

        const primaryDayRegistration = dayRegistrationsPayable?.[0] ?? {
          startTime: '',
          endTime: '',
          duration: 0,
          unit: useDayInputs.value
            ? TimesheetRegistrationDurationUnit.Days
            : TimesheetRegistrationDurationUnit.Hours,
          comments: '',
        }

        /**
         * Our timesheet represents each date as a single object
         * Therefore, table entries are not a 1:1 of timesheet registrations
         * We need to store the data in a way that is easy to display in the table
         * Fx. the break time is actually recorded in it's own TimesheetRegistration
         * but we combine it into a single entry in the table
         */
        let dayObject: DateEntry // Declare it outside

        if (isDateDisabled(date.format('YYYY-MM-DD'))) {
          dayObject = {
            unit:
              primaryDayRegistration.unit ||
              (useDayInputs.value
                ? TimesheetRegistrationDurationUnit.Days
                : TimesheetRegistrationDurationUnit.Hours),
            startTime: '',
            endTime: '',
            duration: 0,
            break: 0,
            comments: '',
            lines: {},
          }
        } else {
          // Total duration for the day should be all combined time
          const durationForDay =
            (dayRegistrationsPayable?.reduce((acc, curr) => acc + curr.duration, 0) || 0) +
            (dayRegistrationsNonPayable?.reduce((acc, curr) => acc + curr.duration, 0) || 0)

          dayObject = {
            unit:
              primaryDayRegistration.unit ||
              (useDayInputs.value
                ? TimesheetRegistrationDurationUnit.Days
                : TimesheetRegistrationDurationUnit.Hours),
            startTime: primaryDayRegistration.startTime || '',
            endTime: primaryDayRegistration.endTime || '',
            duration: durationForDay || 0,
            break: breakTime || 0,
            comments: primaryDayRegistration.comments || '',
            lines: {},
          }
        }

        tableDateEntries.value[date.format('YYYY-MM-DD')] = dayObject

        const lines = {}

        // Add the timesheet lines and also the line registrations
        registrationsFromApiOnDate
          ?.filter(
            (registrationFromApi) => registrationFromApi.type === TimesheetRegistrationType.Line,
          )
          ?.forEach((registrationFromApi) => {
            const { duration } = registrationFromApi
            const { customFieldValues } = registrationFromApi
            const fieldValues: CustomFieldTypeValueInput[] = []
            const fieldTitles: string[] = []

            // Map field values into the format needed when adding timesheet lines
            customFieldValues.forEach((customFieldValue) => {
              if (customFieldValue.displayValue) {
                fieldTitles.push(customFieldValue.displayValue)
              }
              if (customFieldValue.customField.fieldType === 'FREE_TEXT') {
                fieldValues.push({
                  freeText: {
                    id: customFieldValue.customField.id,
                    value: customFieldValue.content,
                  },
                })
              }
              if (customFieldValue.customField.fieldType === 'SINGLE_SELECT') {
                fieldValues.push({
                  singleSelect: {
                    id: customFieldValue.customField.id,
                    value: customFieldValue.customFieldOption?.id,
                  },
                })
              }
            })

            const key = fieldTitles.join(', ')

            // Add the timesheet line
            timesheetLinesStore.addLine({
              title: key,
              customFieldValues: fieldValues,
              isPayable: registrationFromApi.isBillable,
            })

            lines[key] = {
              duration: (lines[key]?.duration || 0) + duration,
            }
          })
        tableDateEntries.value[date.format('YYYY-MM-DD')].lines = lines
      })
    })
  })
  // get total of durations in persistedTimesheetRegistrations
  const persistedTimesheetRegistrationsTotalDuration = computed(() =>
    persistedTimesheetRegistrations.value.reduce(
      // @ts-ignore
      (accumulator, currentRegistration) => accumulator + currentRegistration.duration,
      0,
    ),
  )
  // get total od durations from the formattedTimesheetRegistrationsStore.registrations
  const currentTimesheetRegistrationsTotalDuration = computed(() =>
    formattedTimesheetRegistrationsStore.registrations.reduce(
      // @ts-ignore
      (accumulator, currentRegistration) => accumulator + currentRegistration.duration,
      0,
    ),
  )
  const hasUnsavedChanges = computed(
    () =>
      persistedTimesheetRegistrationsTotalDuration.value !==
      currentTimesheetRegistrationsTotalDuration.value,
  )

  const weeklyReportedTime = computed<{ [weekNumber: string]: number }>(() =>
    Object.keys(weekRange.value).reduce((accumulator, weekNumber) => {
      const weekTotal = weekRange.value[weekNumber].dates.reduce((runningTotal, date) => {
        const dayDuration = tableDateEntries.value[date.format('YYYY-MM-DD')]?.duration
        return formatFixedDecimal(dayDuration ? runningTotal + dayDuration : runningTotal)
      }, 0)

      return {
        ...accumulator,
        [weekNumber]: weekTotal,
      }
    }, {}),
  )

  const sumsForActiveWeek = computed(() => {
    const weekDateStrings = activeWeek.value.dates.map((date) => date.format('YYYY-MM-DD'))

    const weeklyLineTotals = timesheetLinesStore.timesheetLines.reduce((acc, line) => {
      const totalDuration = weekDateStrings.reduce((sum, dateString) => {
        const duration = tableDateEntries?.value?.[dateString]?.lines?.[line.title]?.duration
        return formatFixedDecimal(sum + (duration || 0)) // Round intermediate sums
      }, 0)

      acc[line.title] = formatFixedDecimal(totalDuration) // Round the final total for this line
      return acc
    }, {})

    return {
      total: formatFixedDecimal(weeklyReportedTime.value[activeWeekNumber.value]), // Round total for the week
      ...weeklyLineTotals,
    }
  })

  /**
   * Calculate total reported time within a start and end date range
   */
  const totalReportedTime = computed(() =>
    Object.values(weeklyReportedTime.value).reduce(
      (accumulator, currentWeekTotal) => accumulator + currentWeekTotal,
      0,
    ),
  )

  const totalNonPayableTime = computed(() => {
    let total = 0

    Object.values(tableDateEntries.value).forEach((day) => {
      if (!day.lines) {
        return
      }

      // Cross-reference each line's key with timesheetLinesStore.timesheetLines
      Object.entries(day.lines).forEach(([lineKey, lineValue]) => {
        const matchingLine = timesheetLinesStore.timesheetLines.find(
          (timesheetLine) => timesheetLine.title === lineKey,
        )

        // Check if isPayable is false and add its duration
        if (matchingLine && matchingLine.isPayable === false) {
          total += lineValue.duration
        }
      })
    })

    return total
  })

  const totalPayableTime = computed(() =>
    Math.max(0, totalReportedTime.value - totalNonPayableTime.value),
  )

  const associatedOvertimes: ComputedRef<
    {
      rate_multiplier: number
      duration: number
      total: number
      contributingPaymentRequests: Set<string>
    }[]
  > = computed(() => {
    const overtimeTotals: {
      rate_multiplier: number
      duration: number
      total: number
      contributingPaymentRequests: Set<string>
    }[] = []
    const overtimes = timesheet.value?.overtimes?.data || []
    const multipliers = [...new Set(overtimes.map((overtime) => overtime.rateMultiplier))]
    multipliers.forEach((multiplier) => {
      const duration: number = overtimes
        .filter((overtime) => overtime.rateMultiplier === multiplier)
        .reduce((accumulator, currentOvertime) => accumulator + currentOvertime.duration, 0)
      const total: number = overtimes
        .filter((overtime) => overtime.rateMultiplier === multiplier)
        .reduce((accumulator, currentOvertime) => accumulator + currentOvertime.total, 0)
      const contributingPaymentRequests: Set<string> = new Set()
      overtimes.forEach((overtime) => {
        if (overtime.rateMultiplier === multiplier) {
          overtime.contributingPaymentRequestNumbers.forEach((paymentRequestId) =>
            contributingPaymentRequests.add(paymentRequestId),
          )
        }
      })
      overtimeTotals.push({
        rate_multiplier: multiplier,
        duration,
        total,
        contributingPaymentRequests,
      })
    })

    return overtimeTotals
  })

  const totalPayableTimeMinusOvertime = computed(() => {
    const overtimeDuration = associatedOvertimes.value.reduce(
      (accumulator, currentOvertime) => accumulator + currentOvertime.duration,
      0,
    )
    // Overtime could be built up based on other timesheets which surpasses the amount of this timesheet
    return Math.max(0, totalPayableTime.value - overtimeDuration)
  })

  const updatePersistentTimesheetRegistrations = (registrations: []) => {
    persistedTimesheetRegistrations.value = registrations || []
  }
  const rollbackTimesheetRegistrations = () => {
    timesheetLinesStore.timesheetLines = []
    if (timesheet?.value?.registrations?.data) {
      timesheet.value = {
        ...timesheet.value,
        registrations: { data: persistedTimesheetRegistrations.value },
        overtimes: { data: [] },
      }
    }
  }
  const clearTimesheet = () => {
    if (timesheet?.value?.registrations?.data) {
      timesheet.value = {
        id: '',
        startDate: null,
        endDate: null,
        createdAt: null,
        registrations: {
          data: [],
        },
        overtimes: {
          data: [],
        },
      }
    }
  }

  const jumpToWeekNumber = (weekNumber: number) => {
    selectedWeekNumber.value = weekNumber
    // @ts-ignore
    userSelectedWeekNumber.value = parseInt(weekNumber, 10)
  }

  const jumpToWeekContainingDate = (date: string) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const weekNumber = Object.keys(weekRange.value).find((weekNumber) =>
      weekRange.value[weekNumber].dates.find((weekDate) => weekDate.format('YYYY-MM-DD') === date),
    )
    // @ts-ignore
    jumpToWeekNumber(parseInt(weekNumber, 10))
  }

  const hydrateFromTimesheetData = (timesheetData: Timesheet | null | undefined) => {
    if (timesheetData) {
      timesheet.value = timesheetData
      // @ts-ignore
      persistedTimesheetRegistrations.value = timesheetData.registrations?.data || []
      jumpToWeekNumber(parseInt(Object.keys(weekRange.value)[0], 10))
    }
  }

  const comment = ref('')
  const showCommentInput = reactive({ date: '', show: false })

  const sumOfTimePerDate = computed(() => {
    const sumOfTime = {}
    Object.keys(tableDateEntries.value).forEach((date) => {
      sumOfTime[date] = useDetailedView.value
        ? calculateHoursFromStartAndEndTime(
            tableDateEntries.value[date].startTime,
            tableDateEntries.value[date].endTime,
            tableDateEntries.value[date].break,
          ) || 0
        : tableDateEntries.value[date].duration
    })
    return sumOfTime
  })

  return {
    sumOfTimePerDate,
    timesheetId,
    isLoadingTimesheet,
    externallyImposedJobName,
    externallyImposedCompanyName,
    jobName,
    companyName,
    workerName,
    issuedAt,
    hasPersistedTimesheet,
    isViewOnly,
    useDetailedView,
    useDayInputs,
    weekRange,
    firstWeekNumberInRange,
    userSelectedWeekNumber,
    activeWeek,
    sumsForActiveWeek,
    tableDateEntries,
    weeklyReportedTime,
    totalReportedTime,
    totalNonPayableTime,
    totalPayableTime,
    isPersisted,
    externallyImposedStartDate,
    externallyImposedEndDate,
    dateStart,
    dateEnd,
    rateType,
    detailedView,
    hasUnsavedChanges,
    selectedWeekNumber,
    associatedOvertimes,
    totalPayableTimeMinusOvertime,
    hydrateFromTimesheetData,
    rollbackTimesheetRegistrations,
    updatePersistentTimesheetRegistrations,
    clearTimesheet,
    isDateDisabled,
    jumpToWeekNumber,
    jumpToWeekContainingDate,
    showCommentInput,
    comment,
  }
})
